import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState } from "react";
import { toast } from "sonner";
import type { CreateFormOutput } from "../../forms/myExplorations/CreateExplorationForm";
import type { EditFormOutput } from "../../forms/myExplorations/EditExplorationForm";

export const useMyExplorations = ({
  archived = false,
}: { archived?: boolean } = {}) => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    loadMyExplorations: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  }>({
    loadMyExplorations: false,
    create: false,
    edit: false,
    delete: false,
  });

  const loadMyExplorations = async (
    setExplorations: (data: {
      explorations: any[];
      classes: any[];
      rotations: any[];
      sessions: any[];
      teachers: any[];
    }) => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, loadMyExplorations: true }));
      const response = await api(
        `/explorations/me?shape=teacher${archived ? "&archived=true" : ""}`,
        { method: "GET" },
        dispatch,
      );
      setExplorations({
        explorations: response.explorations,
        classes: response.classes,
        rotations: response.rotations,
        sessions: response.sessions,
        teachers: response.teachers,
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, loadMyExplorations: false }));
    }
  };

  const createExploration = async (
    data: CreateFormOutput,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, create: true }));
      const response = await api(
        "/explorations",
        { method: "POST", body: data },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
      return {
        exploration: response.exploration,
        explorations: response.explorations,
        classes: response.classes,
      };
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const editExploration = async (
    data: EditFormOutput,
    explorationId: string,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, edit: true }));
      const response = await api(
        `/explorations/${explorationId}`,
        { method: "PUT", body: data },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
      return { exploration: response.exploration, classes: response.classes };
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, edit: true }));
    }
  };

  const deleteExploration = async (explorationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      const response = await api(
        `/explorations/${explorationId}`,
        { method: "DELETE" },
        dispatch,
      );
      toast.success(response.message);
      return {
        explorationId: response.explorationId,
        explorationIds: response.explorationIds,
        classes: response.classes,
      };
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  return {
    loadMyExplorations,
    createExploration,
    editExploration,
    deleteExploration,
    loading,
  };
};
